import { authenticatedProApiFetch } from './pro-api.client'

export type AlertSeverity = 'critical' | 'warning' | 'info'
export type AlertType     = 'fault' | 'incident' | 'alert'
export type AlertStatus   = 'active' | 'acknowledged'

export interface FleetAlert {
  id: string
  type: AlertType
  severity: AlertSeverity
  message: string
  description?: string | null
  siteId: string
  siteName: string
  deviceId?: string | null
  deviceName?: string | null
  firstSeen: string   // ISO 8601
  lastSeen: string    // ISO 8601
  status: AlertStatus
  acknowledgedBy?: string | null
  acknowledgedAt?: string | null
}

export interface FetchAlertsParams {
  status?: AlertStatus | 'all'
  type?: AlertType | 'all'
  search?: string
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const _now = Date.now()
const ago = (minutes: number) => new Date(_now - minutes * 60_000).toISOString()

export const MOCK_ALERTS: FleetAlert[] = [
  // ── Critical ──────────────────────────────────────────────────────────────
  {
    id: 'a1', type: 'fault', severity: 'critical',
    message: 'Over-Current Discharge',
    description: 'Load drawing too much power from battery',
    siteId: 'site-003', siteName: 'Carlos Chen',
    deviceId: 'inv-1', deviceName: 'INV-01',
    firstSeen: ago(135), lastSeen: ago(2), status: 'active',
  },
  {
    id: 'a2', type: 'fault', severity: 'critical',
    message: 'BMS Over-Temperature',
    description: 'Battery temperature above safe operating threshold',
    siteId: 'site-029', siteName: 'Cole Delgado',
    deviceId: 'bat-3', deviceName: 'BAT-03',
    firstSeen: ago(45), lastSeen: ago(1), status: 'active',
  },
  {
    id: 'a3', type: 'incident', severity: 'critical',
    message: 'Site Offline',
    description: 'No telemetry received; device may have lost power or connectivity',
    siteId: 'site-016', siteName: 'Priya Patel',
    deviceId: null, deviceName: null,
    firstSeen: ago(182), lastSeen: ago(5), status: 'active',
  },
  // ── Warning ───────────────────────────────────────────────────────────────
  {
    id: 'a4', type: 'fault', severity: 'warning',
    message: 'Grid Low Frequency',
    description: 'Grid input frequency below minimum threshold',
    siteId: 'site-003', siteName: 'Carlos Chen',
    deviceId: 'inv-1', deviceName: 'INV-01',
    firstSeen: ago(60), lastSeen: ago(10), status: 'active',
  },
  {
    id: 'a5', type: 'fault', severity: 'warning',
    message: 'Battery Temperature High',
    description: 'Battery pack temperature approaching upper limit',
    siteId: 'site-029', siteName: 'Cole Delgado',
    deviceId: 'bat-1', deviceName: 'BAT-01',
    firstSeen: ago(25), lastSeen: ago(3), status: 'active',
  },
  {
    id: 'a6', type: 'incident', severity: 'warning',
    message: 'Grid Down',
    description: 'Site is operating on battery backup',
    siteId: 'site-021', siteName: 'Ethan Brooks',
    deviceId: null, deviceName: null,
    firstSeen: ago(18), lastSeen: ago(1), status: 'active',
  },
  {
    id: 'a7', type: 'fault', severity: 'warning',
    message: 'SOC Near Minimum',
    description: 'State of charge approaching minimum threshold (12%)',
    siteId: 'site-016', siteName: 'Priya Patel',
    deviceId: 'bat-2', deviceName: 'BAT-02',
    firstSeen: ago(90), lastSeen: ago(5), status: 'active',
  },
  {
    id: 'a8', type: 'fault', severity: 'warning',
    message: 'Inverter Fan Fault',
    description: 'Cooling fan not detected; thermal shutdown risk',
    siteId: 'site-011', siteName: 'Sun Valley Industrial',
    deviceId: 'inv-2', deviceName: 'INV-02',
    firstSeen: ago(240), lastSeen: ago(30), status: 'active',
  },
  // ── Info ──────────────────────────────────────────────────────────────────
  {
    id: 'a9', type: 'alert', severity: 'info',
    message: 'Firmware Update Available',
    description: 'WCM v2.2.9 is available for this device',
    siteId: 'site-003', siteName: 'Carlos Chen',
    deviceId: 'inv-1', deviceName: 'INV-01',
    firstSeen: ago(1440), lastSeen: ago(1440), status: 'active',
  },
  {
    id: 'a10', type: 'alert', severity: 'info',
    message: 'Scheduled Maintenance Due',
    description: 'Annual inspection recommended for this system',
    siteId: 'site-029', siteName: 'Cole Delgado',
    deviceId: null, deviceName: null,
    firstSeen: ago(2880), lastSeen: ago(2880), status: 'active',
  },
  {
    id: 'a11', type: 'alert', severity: 'info',
    message: 'Export Limit Reached',
    description: 'Site has reached its monthly grid export cap',
    siteId: 'site-021', siteName: 'Ethan Brooks',
    deviceId: null, deviceName: null,
    firstSeen: ago(360), lastSeen: ago(60), status: 'active',
  },
  // ── Acknowledged examples ─────────────────────────────────────────────────
  {
    id: 'a12', type: 'fault', severity: 'critical',
    message: 'DC Overvoltage',
    description: 'DC bus voltage above safe limit',
    siteId: 'site-011', siteName: 'Sun Valley Industrial',
    deviceId: 'inv-1', deviceName: 'INV-01',
    firstSeen: ago(480), lastSeen: ago(120), status: 'acknowledged',
    acknowledgedBy: 'Sarah Kim', acknowledgedAt: ago(115),
  },
  {
    id: 'a13', type: 'incident', severity: 'warning',
    message: 'Grid Down',
    description: 'Site operated on backup for 22 minutes',
    siteId: 'site-003', siteName: 'Carlos Chen',
    deviceId: null, deviceName: null,
    firstSeen: ago(600), lastSeen: ago(378), status: 'acknowledged',
    acknowledgedBy: 'Mike Newbry', acknowledgedAt: ago(370),
  },
]

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchFleetAlerts(
  params: FetchAlertsParams,
  accessToken: string,
): Promise<{ data: FleetAlert[]; total: number }> {
  // TODO: replace with real endpoint once Chase's service is ready
  // return authenticatedProApiFetch('/fleet/alerts', accessToken, { method: 'GET', ... })
  void authenticatedProApiFetch
  void accessToken

  let results = [...MOCK_ALERTS]

  if (params.status && params.status !== 'all') {
    results = results.filter(a => a.status === params.status)
  }
  if (params.type && params.type !== 'all') {
    results = results.filter(a => a.type === params.type)
  }
  if (params.search) {
    const q = params.search.trim().toLowerCase()
    results = results.filter(a =>
      a.message.toLowerCase().includes(q) ||
      a.siteName.toLowerCase().includes(q) ||
      (a.deviceName ?? '').toLowerCase().includes(q) ||
      (a.description ?? '').toLowerCase().includes(q),
    )
  }

  return { data: results, total: results.length }
}

export async function acknowledgeAlert(
  alertId: string,
  accessToken: string,
): Promise<{ alert: FleetAlert }> {
  // TODO: replace with real endpoint
  // return authenticatedProApiFetch(`/fleet/alerts/${alertId}/acknowledge`, accessToken, { method: 'POST' })
  void accessToken

  const found = MOCK_ALERTS.find(a => a.id === alertId)
  if (!found) throw new Error(`Alert ${alertId} not found`)
  return {
    alert: {
      ...found,
      status: 'acknowledged',
      acknowledgedAt: new Date().toISOString(),
    },
  }
}
